/**
 * @fileoverview Custom hook for checking hidden, disabled and
 * required fields based on field rules from config.
 */

import { EntityFieldRules } from '~/jobiqo/models/config';

export function matchesRule(value: any, rule: any): boolean {
  const checkValue = (val: any, ruleProperty: any): boolean => {
    if (ruleProperty && val && Array.isArray(ruleProperty)) {
      return ruleProperty.includes(val);
    }
    return (
      val !== undefined &&
      ruleProperty !== undefined &&
      (val === ruleProperty || (val === null && ruleProperty === null))
    );
  };

  return (
    checkValue(value, rule) ||
    checkValue(value?.tid?.toString(), rule?.tid) ||
    checkValue(value?.parent?.tid?.toString(), rule?.tid) ||
    checkValue(value?.name, rule?.name) ||
    checkValue(value?.parent?.name, rule?.name) ||
    checkValue(value?.label, rule?.name) ||
    checkValue(value?.parent?.label, rule?.name)
  );
}

/**
 * This function calculates a property based on the provided field rules and values. It checks
 * if a field should be either disabled, required or hidden based on the property provided.
 *
 * @param {string} property
 *  The property to calculate.
 * @param {string} id
 *  The id of the field rules to use.
 * @param {any} fieldRules
 *  The field rules to use for calculation.
 * @param {any} values
 *  The values to check against the field rules.
 *
 * @returns {boolean}
 *  Returns true if a match is found, false otherwise.
 */
export const calculateProperty = (
  property: string,
  id: string,
  fieldRules: any,
  values: any,
) => {
  if (fieldRules?.[id]?.[property]) {
    for (const field in fieldRules?.[id]?.[property]) {
      if (!Array.isArray(values[field])) {
        // When its not an array we can just check if the value matches.
        if (matchesRule(values[field], fieldRules?.[id]?.[property][field])) {
          return true;
        }
      } else {
        // If the array is the same we can return true (e.g. empty array to check for empty fields).
        if (
          JSON.stringify(values[field]) ===
          JSON.stringify(fieldRules?.[id]?.[property][field])
        ) {
          return true;
        }

        // When an array if one value matches, we can return true.
        for (const value of values[field]) {
          if (matchesRule(value, fieldRules?.[id]?.[property][field])) {
            return true;
          }
        }
      }
    }
  }
  return false;
};

/**
 * This function checks if a taxonomy subtree should be hidden
 * based on the field rules.
 *
 * @param id
 *  The id of the field.
 * @param fieldRules
 *  The field rules to check against.
 * @param values
 *  The values of the form.
 * @returns
 *  Returns an array of term ids to hide or null if no terms should be hidden.
 */
export const shouldHideSubtree = (
  id: string,
  fieldRules: any,
  values: any,
): string[] | null => {
  let hiddenTermIds: string[] = [];
  if (fieldRules?.[id]?.hideTerms) {
    fieldRules[id].hideTerms.forEach(
      ({ terms, rule }: { terms: string[]; rule: any }) => {
        for (const field in rule) {
          if (values[field]) {
            if (!Array.isArray(values[field])) {
              // When its not an array we can just check if the value matches.
              if (matchesRule(values[field], rule[field])) {
                hiddenTermIds = [...hiddenTermIds, ...terms];
              }
            } else {
              // When an array if one value matches, we can return true.
              for (const value of values[field]) {
                if (matchesRule(value, rule[field])) {
                  hiddenTermIds = [...hiddenTermIds, ...terms];
                }
              }
            }
          }
        }
      },
    );
  }
  return hiddenTermIds.length > 0 ? hiddenTermIds : null;
};

/**
 * This hook checks if a field should be hidden, disabled or required based on the field rules
 *
 * @param id
 *  The id of the field.
 * @param values
 *  The values of the form.
 * @param fieldRules
 *  The field rules to check against.
 *
 * @returns
 *  Returns an object with the hidden, disabled and required properties.
 */
export function useFieldRules(
  id: string,
  values: any,
  fieldRules: EntityFieldRules,
) {
  const hidden = calculateProperty('hidden', id, fieldRules, values);
  const disabled = calculateProperty('disabled', id, fieldRules, values);
  const required = calculateProperty('required', id, fieldRules, values);
  const hideSubTreeElements = shouldHideSubtree(id, fieldRules, values);

  return {
    hidden,
    disabled,
    required,
    hideSubTreeElements,
  };
}
